import { createPortal } from "react-dom";
import { OwcModalDialog, OwcTypography, OwcButton } from "@one/react";
import { react18WorkaroundProps } from "../../../utils/constants/constants";
import { Eventcalendar, momentTimezone } from "@mobiscroll/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { useSelector } from "react-redux";
import { withApollo } from "react-apollo";
import { BACKDROP_ZINDEX, TIMELINE_TYPE } from "../../../constants";
import { UPDATE_BOOKING } from "../../../gql/bookingapi/mutations";
import BackdropPortal from "../../../components/shared/BackdropPortal";
import { RootStore } from "../../../reducers";
import { ChangeBookingEndTimeModalProps } from "./types";
import { TIMELINE_BUFFER, VIEW_CONFIG } from "./constants";
import { quickEditTracker } from "../../../features/booking-operations/quickEditTracker";
import { buildUpdateBookingVariables } from "./utils/mutationUtils";
import useResourceSelection from "./hooks/useResourceSelection";
import useCalendarNavigation from "./hooks/useCalendarNavigation";
import useOtherBookings from "./hooks/useOtherBookings";
import { createCurrentBookingEvents, createResources } from "./utils/eventsUtils";
import useEventResizing from "./hooks/useEventResizing";
import useCalendarViewState from "./hooks/useCalendarViewState";
import useCalendarRenderers from "./hooks/useCalendarRenderers";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import "./ChangeBookingEndTimeModal.scss";

const ChangeBookingEndTimeModal: React.FC<ChangeBookingEndTimeModalProps> = ({
  isOpen,
  onClose,
  booking,
  onSave,
  client,
  initialSelectedResourceId = null,
}) => {
  const user = useSelector(
    (store: RootStore) => store.user as RootStore["user"] & { site?: string; user?: string; givenName?: string; familyName?: string },
  );
  const siteTimezone = user?.timezone || "Europe/Zurich";
  const site = user?.site || "";
  const [saving, setSaving] = useState(false);

  const calendarRef = useRef<{ navigate: (date: Date) => void } | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const { calendarInstance, view, handleCalendarInit, handleViewChange } = useCalendarViewState(isOpen);

  const {
    selectedCount,
    toggleResource,
    selectAll,
    isSelected,
  } = useResourceSelection(booking, isOpen, initialSelectedResourceId);

  const minAllowedEndTime = useMemo(() => {
    const now = moment().tz(siteTimezone);
    return now.clone().minutes(Math.floor(now.minutes() / 30) * 30).seconds(0).milliseconds(0);
  }, [siteTimezone]);

  const { navigateToToday, navigateToPrev, navigateToNext } = useCalendarNavigation(
    calendarInstance,
    selectedDate,
    setSelectedDate,
    view,
    siteTimezone,
  );
  const { otherBookings } = useOtherBookings(booking, site, selectedDate, view, siteTimezone, client);

  const resources = useMemo(() => createResources(booking), [booking]);
  const currentBookingEvents = useMemo(() => createCurrentBookingEvents(booking), [booking]);
  const isAllSelected = resources.length > 0 && selectedCount === resources.length;
  const isIndeterminate = selectedCount > 0 && selectedCount < resources.length;

  useEffect(() => {
    if (!isOpen) {
      setSelectedDate(null);
      return;
    }
    const date = booking?.dateFrom
      ? moment(booking.dateFrom).tz(siteTimezone).toDate()
      : moment().tz(siteTimezone).toDate();
    setSelectedDate(date);
  }, [isOpen, booking?.dateFrom, siteTimezone]);

  const handleVisibleChange = useCallback(
    (e: CustomEvent) => {
      e.stopPropagation();
      if (!(e.target as HTMLElement & { visible?: boolean }).visible) {
        onClose();
      }
    },
    [onClose],
  );

  const handleSelectAll = useCallback(
    (selected: boolean) => { selectAll(resources, selected); },
    [resources, selectAll],
  );

  const { resizedEntries, hasChanges, adjustedCurrentBookingEvents, handleEventDragStart, handleEventDragEnd, handleEventUpdate } =
    useEventResizing({ otherBookings, isSelected, currentBookingEvents, minAllowedEndTime, isOpen });

  const handleSave = useCallback(async () => {
    if (!booking) { return; }

    const { variables, newDateTo } = buildUpdateBookingVariables(booking, resizedEntries, user);

    setSaving(true);
    try {
      const mutationResult = await client.mutate({ mutation: UPDATE_BOOKING, variables });

      const commandId = mutationResult?.data?.createBookingCommand?.id;
      if (commandId) {
        const trackingPromise = quickEditTracker.register(commandId, "UPDATE", {
          onSuccess: () => {
            setSaving(false);
            if (onSave) { onSave(booking, newDateTo); }
            onClose();
          },
          onError: () => {
            setSaving(false);
            snackbarService.show({ message: "Failed to update booking end time", type: "error", duration: 4000 });
          },
        });
        if (trackingPromise) {
          trackingPromise.catch(() => {});
        }
      } else {
        setSaving(false);
        if (onSave) { onSave(booking, newDateTo); }
        onClose();
      }
    } catch (e) {
      setSaving(false);
      const message = e instanceof Error && e.message
        ? e.message
        : "Failed to update booking end time";
      snackbarService.show({ message, type: "error", duration: 4000 });
    }
  }, [booking, resizedEntries, user, client, onSave, onClose]);

  const eventData = useMemo(
    () => [...adjustedCurrentBookingEvents, ...otherBookings],
    [adjustedCurrentBookingEvents, otherBookings],
  );
  const calView = useMemo(() => VIEW_CONFIG[view] ?? VIEW_CONFIG[TIMELINE_TYPE.DAY], [view]);

  const { renderHeader, renderResource, renderResourceHeader, renderHour, renderScheduleEvent } =
    useCalendarRenderers({
      navigateToToday, navigateToPrev, navigateToNext, handleViewChange, view,
      isSelected, toggleResource, resources, selectedCount, handleSelectAll,
      isAllSelected, isIndeterminate, siteTimezone,
    });

  if (!selectedDate) { return null; }

  const refDate = moment(selectedDate).subtract(TIMELINE_BUFFER.DAY, "days").startOf("day").toDate();

  return createPortal(
    <>
    <BackdropPortal show={saving} zIndex={BACKDROP_ZINDEX + 10} children={undefined} />
    <OwcModalDialog
      disableScrollLock
      visible={isOpen}
      size="lg"
      width="80rem"
      className="owcmodalZIndex"
      onVisibleChange={handleVisibleChange}
      {...react18WorkaroundProps}
    >
      <div slot="header">
        <OwcTypography
          variant="subtitle2"
          {...react18WorkaroundProps}
        >
          Change booking end time
        </OwcTypography>
      </div>

      <div className="change-end-time-modal__content">
        <OwcTypography
          variant="body2"
          {...react18WorkaroundProps}
        >
          Select equipment and drag the timeline to set a new end time. All selected equipment will be updated together.
        </OwcTypography>
        <Eventcalendar
          theme="ios"
          height="100%"
          view={calView}
          resources={resources}
          data={eventData}
          ref={calendarRef}
          refDate={refDate}
          selectedDate={selectedDate}
          onInit={handleCalendarInit}
          renderHeader={renderHeader}
          renderResource={renderResource}
          renderResourceHeader={renderResourceHeader}
          renderHour={renderHour}
          renderScheduleEvent={renderScheduleEvent}
          cssClass="change-end-time-modal"
          dragToCreate={false}
          dragToMove={false}
          dragToResize={true}
          eventOverlap={false}
          dragTimeStep={30}
          invalid={[{ start: new Date(0), end: minAllowedEndTime.toDate() }]}
          onEventDragStart={handleEventDragStart}
          onEventDragEnd={handleEventDragEnd}
          onEventUpdate={handleEventUpdate}
          dataTimezone="utc"
          displayTimezone={siteTimezone}
          timezonePlugin={momentTimezone}
          clickToCreate={false}
          externalDrop={false}
        />
      </div>

      <div slot="actions" className="change-end-time-modal__actions">
        <OwcButton
          variant="secondary"
          onClick={onClose}
          {...react18WorkaroundProps}
        >
          Cancel
        </OwcButton>
        <OwcButton
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanges}
          {...react18WorkaroundProps}
        >
          Save changes
        </OwcButton>
      </div>
    </OwcModalDialog>
    </>,
    document.body,
  );
};

export default withApollo(ChangeBookingEndTimeModal);
